import { useState, useEffect } from 'react'
import {
  Users, Settings, CreditCard, BarChart3, Search, Edit3,
  Check, X, Loader2, Clock, Database, Cpu, Shield,
  ChevronDown, ChevronUp, Save, Mail, Building2
} from 'lucide-react'
import { useAuthStore, isAdminUser, type User } from '@/store/auth'
import { useNavigate } from 'react-router-dom'
import toast from 'react-hot-toast'
import { db } from '@/lib/firebase'
import { collection, getDocs, doc, updateDoc, query, where } from 'firebase/firestore'

interface ClientUser extends User {
  docId: string
  uid: string
}

interface UsageData {
  totalMinutes: number
  totalRecordings: number
  totalVoiceProfiles: number
  openaiTokens: number
  replicateMinutes: number
}

export function Admin() {
  const { user } = useAuthStore()
  const navigate = useNavigate()
  const [activeTab, setActiveTab] = useState<'clients' | 'usage' | 'settings'>('clients')
  const [isLoading, setIsLoading] = useState(true)
  const [clients, setClients] = useState<ClientUser[]>([])
  const [searchQuery, setSearchQuery] = useState('')
  const [editingClient, setEditingClient] = useState<ClientUser | null>(null)
  const [isSaving, setIsSaving] = useState(false)
  const [expandedClient, setExpandedClient] = useState<string | null>(null)
  const [usageData, setUsageData] = useState<Record<string, UsageData>>({})

  // Check admin access
  useEffect(() => {
    if (!isAdminUser(user?.email)) {
      toast.error('Accesso non autorizzato')
      navigate('/')
    }
  }, [user, navigate])

  // Fetch all users
  useEffect(() => {
    const fetchClients = async () => {
      setIsLoading(true)
      try {
        const usersSnapshot = await getDocs(collection(db, 'users'))
        const usersData: ClientUser[] = []

        usersSnapshot.forEach((docSnap) => {
          const data = docSnap.data()
          usersData.push({
            docId: docSnap.id,
            uid: data.uid,
            id: data.uid,
            email: data.email || '',
            name: data.name || data.email?.split('@')[0] || 'Utente',
            avatar: data.avatar,
            plan: data.plan || 'free',
            planName: data.planName,
            planPrice: data.planPrice,
            planStartDate: data.planStartDate,
            minutesUsed: data.minutesUsed || 0,
            minutesLimit: data.minutesLimit || 60,
            voiceIdsUsed: data.voiceIdsUsed || 0,
            voiceIdsLimit: data.voiceIdsLimit || 3,
            storageUsed: data.storageUsed || 0,
            storageLimit: data.storageLimit || 1073741824, // 1GB default
            isAdmin: data.isAdmin || false,
            isActive: data.isActive !== false, // default true
            notes: data.notes || '',
            company: data.company || '',
            openaiTokensUsed: data.openaiTokensUsed || 0,
            replicateMinutesUsed: data.replicateMinutesUsed || 0,
            createdAt: data.createdAt || new Date().toISOString(),
            updatedAt: data.updatedAt,
          })
        })

        setClients(usersData.sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()))
      } catch (error) {
        console.error('Error fetching clients:', error)
        toast.error('Errore nel caricamento utenti')
      } finally {
        setIsLoading(false)
      }
    }

    if (isAdminUser(user?.email)) {
      fetchClients()
    }
  }, [user])

  // Fetch usage data for a specific client
  const fetchClientUsage = async (clientId: string) => {
    try {
      // Get recordings count and minutes
      const recordingsQuery = query(collection(db, 'recordings'), where('userId', '==', clientId))
      const recordingsSnapshot = await getDocs(recordingsQuery)

      let totalMinutes = 0
      recordingsSnapshot.forEach((docSnap) => {
        const data = docSnap.data()
        if (data.duration) {
          totalMinutes += data.duration / 60
        }
      })

      // Get voice profiles count
      const voiceQuery = query(collection(db, 'voiceProfiles'), where('userId', '==', clientId))
      const voiceSnapshot = await getDocs(voiceQuery)

      setUsageData(prev => ({
        ...prev,
        [clientId]: {
          totalMinutes: Math.round(totalMinutes),
          totalRecordings: recordingsSnapshot.size,
          totalVoiceProfiles: voiceSnapshot.size,
          openaiTokens: 0, // TODO: track this
          replicateMinutes: Math.round(totalMinutes),
        }
      }))
    } catch (error) {
      console.error('Error fetching usage:', error)
    }
  }

  // Handle client expansion
  const handleExpandClient = (clientId: string) => {
    if (expandedClient === clientId) {
      setExpandedClient(null)
    } else {
      setExpandedClient(clientId)
      if (!usageData[clientId]) {
        fetchClientUsage(clientId)
      }
    }
  }

  // Save client changes
  const handleSaveClient = async () => {
    if (!editingClient) return

    setIsSaving(true)
    try {
      const userRef = doc(db, 'users', editingClient.docId)
      await updateDoc(userRef, {
        name: editingClient.name,
        company: editingClient.company,
        plan: editingClient.plan,
        planName: editingClient.planName,
        planPrice: editingClient.planPrice,
        planStartDate: editingClient.planStartDate,
        minutesLimit: editingClient.minutesLimit,
        voiceIdsLimit: editingClient.voiceIdsLimit,
        storageLimit: editingClient.storageLimit,
        isActive: editingClient.isActive,
        notes: editingClient.notes,
        updatedAt: new Date().toISOString(),
      })

      setClients(prev => prev.map(c => c.docId === editingClient.docId ? editingClient : c))
      setEditingClient(null)
      toast.success('Cliente aggiornato!')
    } catch (error) {
      console.error('Error saving client:', error)
      toast.error('Errore nel salvataggio')
    } finally {
      setIsSaving(false)
    }
  }

  // Format bytes to human readable
  const formatBytes = (bytes: number) => {
    if (bytes === 0) return '0 B'
    const k = 1024
    const sizes = ['B', 'KB', 'MB', 'GB']
    const i = Math.floor(Math.log(bytes) / Math.log(k))
    return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i]
  }

  // Filter clients
  const filteredClients = clients.filter(client =>
    client.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    client.email.toLowerCase().includes(searchQuery.toLowerCase()) ||
    client.company?.toLowerCase().includes(searchQuery.toLowerCase())
  )

  // Admin access check
  if (!isAdminUser(user?.email)) {
    return null
  }

  return (
    <div className="max-w-7xl mx-auto">
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center gap-3 mb-2">
          <div className="p-2 bg-red-100 rounded-lg">
            <Shield className="h-6 w-6 text-red-600" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-slate-900">Admin Panel</h1>
            <p className="text-slate-500 text-sm">Gestione clienti e configurazione piani custom</p>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 mb-6 border-b border-slate-200 pb-4">
        <button
          onClick={() => setActiveTab('clients')}
          className={`px-4 py-2 rounded-lg text-sm font-medium transition-all flex items-center gap-2 ${
            activeTab === 'clients'
              ? 'bg-slate-900 text-white shadow-md'
              : 'text-slate-600 hover:bg-slate-100'
          }`}
        >
          <Users className="h-4 w-4" /> Gestione Clienti
        </button>
        <button
          onClick={() => setActiveTab('usage')}
          className={`px-4 py-2 rounded-lg text-sm font-medium transition-all flex items-center gap-2 ${
            activeTab === 'usage'
              ? 'bg-slate-900 text-white shadow-md'
              : 'text-slate-600 hover:bg-slate-100'
          }`}
        >
          <BarChart3 className="h-4 w-4" /> Utilizzo Globale
        </button>
        <button
          onClick={() => setActiveTab('settings')}
          className={`px-4 py-2 rounded-lg text-sm font-medium transition-all flex items-center gap-2 ${
            activeTab === 'settings'
              ? 'bg-slate-900 text-white shadow-md'
              : 'text-slate-600 hover:bg-slate-100'
          }`}
        >
          <Settings className="h-4 w-4" /> Impostazioni
        </button>
      </div>

      {/* Content */}
      {activeTab === 'clients' && (
        <div className="space-y-6">
          {/* Search & Stats Bar */}
          <div className="flex items-center justify-between gap-4">
            <div className="relative flex-1 max-w-md">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Cerca cliente per nome, email o azienda..."
                className="w-full pl-10 pr-4 py-2.5 bg-white border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-slate-900/10 focus:border-slate-900"
              />
            </div>
            <div className="flex items-center gap-4 text-sm text-slate-500">
              <span className="flex items-center gap-1.5">
                <Users className="h-4 w-4" />
                <strong className="text-slate-900">{clients.length}</strong> clienti totali
              </span>
              <span className="flex items-center gap-1.5">
                <Check className="h-4 w-4 text-emerald-500" />
                <strong className="text-slate-900">{clients.filter(c => c.isActive).length}</strong> attivi
              </span>
            </div>
          </div>

          {/* Clients List */}
          <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
            {isLoading ? (
              <div className="p-12 text-center">
                <Loader2 className="h-8 w-8 animate-spin text-slate-400 mx-auto mb-3" />
                <p className="text-slate-500">Caricamento clienti...</p>
              </div>
            ) : filteredClients.length === 0 ? (
              <div className="p-12 text-center">
                <Users className="h-12 w-12 text-slate-300 mx-auto mb-3" />
                <p className="text-slate-500">Nessun cliente trovato</p>
              </div>
            ) : (
              <div className="divide-y divide-slate-100">
                {filteredClients.map((client) => (
                  <div key={client.docId} className="hover:bg-slate-50/50 transition-colors">
                    {/* Client Row */}
                    <div
                      className="p-4 flex items-center justify-between cursor-pointer"
                      onClick={() => handleExpandClient(client.uid)}
                    >
                      <div className="flex items-center gap-4">
                        <div className={`h-10 w-10 rounded-full flex items-center justify-center text-white font-bold ${
                          client.isActive ? 'bg-gradient-to-br from-slate-700 to-slate-900' : 'bg-slate-300'
                        }`}>
                          {client.name.charAt(0).toUpperCase()}
                        </div>
                        <div>
                          <div className="flex items-center gap-2">
                            <h4 className="font-semibold text-slate-900">{client.name}</h4>
                            {!client.isActive && (
                              <span className="text-[10px] font-bold bg-red-100 text-red-600 px-1.5 py-0.5 rounded">
                                DISATTIVO
                              </span>
                            )}
                            {client.isAdmin && (
                              <span className="text-[10px] font-bold bg-purple-100 text-purple-600 px-1.5 py-0.5 rounded">
                                ADMIN
                              </span>
                            )}
                          </div>
                          <div className="flex items-center gap-3 text-xs text-slate-500 mt-0.5">
                            <span className="flex items-center gap-1">
                              <Mail className="h-3 w-3" /> {client.email}
                            </span>
                            {client.company && (
                              <span className="flex items-center gap-1">
                                <Building2 className="h-3 w-3" /> {client.company}
                              </span>
                            )}
                          </div>
                        </div>
                      </div>

                      <div className="flex items-center gap-6">
                        {/* Plan Badge */}
                        <div className="text-right">
                          <span className={`text-xs font-bold px-2 py-1 rounded ${
                            client.plan === 'enterprise' ? 'bg-purple-100 text-purple-700' :
                            client.plan === 'business' ? 'bg-blue-100 text-blue-700' :
                            client.plan === 'pro' ? 'bg-emerald-100 text-emerald-700' :
                            client.plan === 'custom' ? 'bg-amber-100 text-amber-700' :
                            'bg-slate-100 text-slate-600'
                          }`}>
                            {client.planName || client.plan.toUpperCase()}
                          </span>
                          {client.planPrice && (
                            <p className="text-xs text-slate-400 mt-1">{client.planPrice}€/mese</p>
                          )}
                        </div>

                        {/* Usage Preview */}
                        <div className="text-right min-w-[100px]">
                          <p className="text-sm font-semibold text-slate-900">
                            {client.minutesUsed} / {client.minutesLimit} min
                          </p>
                          <div className="h-1.5 w-24 bg-slate-100 rounded-full mt-1 overflow-hidden">
                            <div
                              className={`h-full rounded-full transition-all ${
                                (client.minutesUsed / client.minutesLimit) > 0.9 ? 'bg-red-500' :
                                (client.minutesUsed / client.minutesLimit) > 0.7 ? 'bg-amber-500' :
                                'bg-emerald-500'
                              }`}
                              style={{ width: `${Math.min(100, (client.minutesUsed / client.minutesLimit) * 100)}%` }}
                            />
                          </div>
                        </div>

                        {/* Actions */}
                        <div className="flex items-center gap-2">
                          <button
                            onClick={(e) => {
                              e.stopPropagation()
                              setEditingClient(client)
                            }}
                            className="p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-lg transition-colors"
                            title="Modifica"
                          >
                            <Edit3 className="h-4 w-4" />
                          </button>
                          {expandedClient === client.uid ? (
                            <ChevronUp className="h-5 w-5 text-slate-400" />
                          ) : (
                            <ChevronDown className="h-5 w-5 text-slate-400" />
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Expanded Details */}
                    {expandedClient === client.uid && (
                      <div className="px-4 pb-4 pt-2 bg-slate-50 border-t border-slate-100">
                        <div className="grid grid-cols-4 gap-4">
                          {/* Usage Stats */}
                          <div className="bg-white rounded-lg p-4 border border-slate-200">
                            <h5 className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-3">Utilizzo</h5>
                            {usageData[client.uid] ? (
                              <div className="space-y-2 text-sm">
                                <div className="flex justify-between">
                                  <span className="text-slate-500">Registrazioni</span>
                                  <span className="font-semibold">{usageData[client.uid].totalRecordings}</span>
                                </div>
                                <div className="flex justify-between">
                                  <span className="text-slate-500">Minuti totali</span>
                                  <span className="font-semibold">{usageData[client.uid].totalMinutes} min</span>
                                </div>
                                <div className="flex justify-between">
                                  <span className="text-slate-500">Voice Profiles</span>
                                  <span className="font-semibold">{usageData[client.uid].totalVoiceProfiles}</span>
                                </div>
                              </div>
                            ) : (
                              <div className="flex items-center justify-center py-4">
                                <Loader2 className="h-5 w-5 animate-spin text-slate-400" />
                              </div>
                            )}
                          </div>

                          {/* Limits */}
                          <div className="bg-white rounded-lg p-4 border border-slate-200">
                            <h5 className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-3">Limiti</h5>
                            <div className="space-y-2 text-sm">
                              <div className="flex justify-between">
                                <span className="text-slate-500">Minuti/mese</span>
                                <span className="font-semibold">{client.minutesLimit}</span>
                              </div>
                              <div className="flex justify-between">
                                <span className="text-slate-500">Voice IDs</span>
                                <span className="font-semibold">{client.voiceIdsLimit}</span>
                              </div>
                              <div className="flex justify-between">
                                <span className="text-slate-500">Storage</span>
                                <span className="font-semibold">{formatBytes(client.storageLimit || 0)}</span>
                              </div>
                            </div>
                          </div>

                          {/* Plan Info */}
                          <div className="bg-white rounded-lg p-4 border border-slate-200">
                            <h5 className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-3">Piano</h5>
                            <div className="space-y-2 text-sm">
                              <div className="flex justify-between">
                                <span className="text-slate-500">Tipo</span>
                                <span className="font-semibold">{client.planName || client.plan}</span>
                              </div>
                              <div className="flex justify-between">
                                <span className="text-slate-500">Prezzo</span>
                                <span className="font-semibold">{client.planPrice ? `${client.planPrice}€/mese` : 'N/A'}</span>
                              </div>
                              <div className="flex justify-between">
                                <span className="text-slate-500">Inizio</span>
                                <span className="font-semibold">
                                  {client.planStartDate ? new Date(client.planStartDate).toLocaleDateString('it-IT') : 'N/A'}
                                </span>
                              </div>
                            </div>
                          </div>

                          {/* Notes */}
                          <div className="bg-white rounded-lg p-4 border border-slate-200">
                            <h5 className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-3">Note</h5>
                            <p className="text-sm text-slate-600">
                              {client.notes || 'Nessuna nota'}
                            </p>
                            <p className="text-xs text-slate-400 mt-2">
                              Creato: {new Date(client.createdAt).toLocaleDateString('it-IT')}
                            </p>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {activeTab === 'usage' && (
        <div className="space-y-6">
          {/* Global Stats Cards */}
          <div className="grid grid-cols-4 gap-4">
            <div className="bg-white rounded-xl border border-slate-200 p-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="p-2 bg-blue-100 rounded-lg">
                  <Clock className="h-5 w-5 text-blue-600" />
                </div>
                <span className="text-sm font-medium text-slate-500">Minuti Totali</span>
              </div>
              <p className="text-3xl font-bold text-slate-900">
                {clients.reduce((sum, c) => sum + c.minutesUsed, 0).toLocaleString()}
              </p>
              <p className="text-xs text-slate-400 mt-1">
                su {clients.reduce((sum, c) => sum + c.minutesLimit, 0).toLocaleString()} disponibili
              </p>
            </div>

            <div className="bg-white rounded-xl border border-slate-200 p-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="p-2 bg-emerald-100 rounded-lg">
                  <Users className="h-5 w-5 text-emerald-600" />
                </div>
                <span className="text-sm font-medium text-slate-500">Clienti Attivi</span>
              </div>
              <p className="text-3xl font-bold text-slate-900">
                {clients.filter(c => c.isActive).length}
              </p>
              <p className="text-xs text-slate-400 mt-1">
                su {clients.length} registrati
              </p>
            </div>

            <div className="bg-white rounded-xl border border-slate-200 p-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="p-2 bg-purple-100 rounded-lg">
                  <Cpu className="h-5 w-5 text-purple-600" />
                </div>
                <span className="text-sm font-medium text-slate-500">API OpenAI</span>
              </div>
              <p className="text-3xl font-bold text-slate-900">
                {(clients.reduce((sum, c) => sum + (c.openaiTokensUsed || 0), 0) / 1000).toFixed(1)}K
              </p>
              <p className="text-xs text-slate-400 mt-1">tokens utilizzati</p>
            </div>

            <div className="bg-white rounded-xl border border-slate-200 p-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="p-2 bg-amber-100 rounded-lg">
                  <Database className="h-5 w-5 text-amber-600" />
                </div>
                <span className="text-sm font-medium text-slate-500">Storage</span>
              </div>
              <p className="text-3xl font-bold text-slate-900">
                {formatBytes(clients.reduce((sum, c) => sum + (c.storageUsed || 0), 0))}
              </p>
              <p className="text-xs text-slate-400 mt-1">spazio utilizzato</p>
            </div>
          </div>

          {/* Revenue Summary */}
          <div className="bg-white rounded-xl border border-slate-200 p-6">
            <h3 className="font-semibold text-slate-900 mb-4 flex items-center gap-2">
              <CreditCard className="h-5 w-5 text-slate-500" />
              Revenue Mensile Prevista
            </h3>
            <div className="flex items-baseline gap-2">
              <span className="text-4xl font-bold text-slate-900">
                {clients.reduce((sum, c) => sum + (c.planPrice || 0), 0).toLocaleString()}€
              </span>
              <span className="text-slate-400">/mese</span>
            </div>
            <div className="mt-4 grid grid-cols-4 gap-4">
              {['enterprise', 'business', 'pro', 'custom'].map(plan => {
                const count = clients.filter(c => c.plan === plan).length
                const revenue = clients.filter(c => c.plan === plan).reduce((sum, c) => sum + (c.planPrice || 0), 0)
                return (
                  <div key={plan} className="text-center p-3 bg-slate-50 rounded-lg">
                    <p className="text-xs text-slate-500 uppercase">{plan}</p>
                    <p className="font-bold text-slate-900">{count} clienti</p>
                    <p className="text-sm text-slate-600">{revenue}€/mese</p>
                  </div>
                )
              })}
            </div>
          </div>
        </div>
      )}

      {activeTab === 'settings' && (
        <div className="bg-white rounded-xl border border-slate-200 p-6">
          <h3 className="font-semibold text-slate-900 mb-4">Impostazioni Admin</h3>
          <p className="text-slate-500">
            Le impostazioni globali verranno aggiunte qui (piani predefiniti, limiti default, ecc.)
          </p>
        </div>
      )}

      {/* Edit Client Modal */}
      {editingClient && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full overflow-hidden max-h-[90vh] overflow-y-auto">
            <div className="px-6 py-4 border-b border-slate-100 flex items-center justify-between sticky top-0 bg-white z-10">
              <h3 className="font-bold text-lg text-slate-900">Modifica Cliente</h3>
              <button
                onClick={() => setEditingClient(null)}
                className="p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-lg transition-colors"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="p-6 space-y-6">
              {/* Basic Info */}
              <div>
                <h4 className="text-sm font-semibold text-slate-700 mb-3 flex items-center gap-2">
                  <Users className="h-4 w-4" /> Informazioni Base
                </h4>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <label className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Nome</label>
                    <input
                      type="text"
                      value={editingClient.name}
                      onChange={(e) => setEditingClient({ ...editingClient, name: e.target.value })}
                      className="w-full bg-white border border-slate-200 rounded-lg px-4 py-2.5 text-sm"
                    />
                  </div>
                  <div className="space-y-2">
                    <label className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Azienda</label>
                    <input
                      type="text"
                      value={editingClient.company || ''}
                      onChange={(e) => setEditingClient({ ...editingClient, company: e.target.value })}
                      className="w-full bg-white border border-slate-200 rounded-lg px-4 py-2.5 text-sm"
                    />
                  </div>
                  <div className="space-y-2">
                    <label className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Email (readonly)</label>
                    <input
                      type="email"
                      value={editingClient.email}
                      disabled
                      className="w-full bg-slate-50 border border-slate-200 rounded-lg px-4 py-2.5 text-sm text-slate-500"
                    />
                  </div>
                  <div className="space-y-2">
                    <label className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Stato</label>
                    <select
                      value={editingClient.isActive ? 'active' : 'inactive'}
                      onChange={(e) => setEditingClient({ ...editingClient, isActive: e.target.value === 'active' })}
                      className="w-full bg-white border border-slate-200 rounded-lg px-4 py-2.5 text-sm"
                    >
                      <option value="active">Attivo</option>
                      <option value="inactive">Disattivo</option>
                    </select>
                  </div>
                </div>
              </div>

              {/* Plan Configuration */}
              <div>
                <h4 className="text-sm font-semibold text-slate-700 mb-3 flex items-center gap-2">
                  <CreditCard className="h-4 w-4" /> Configurazione Piano
                </h4>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <label className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Tipo Piano</label>
                    <select
                      value={editingClient.plan}
                      onChange={(e) => setEditingClient({ ...editingClient, plan: e.target.value as any })}
                      className="w-full bg-white border border-slate-200 rounded-lg px-4 py-2.5 text-sm"
                    >
                      <option value="free">Free</option>
                      <option value="pro">Pro</option>
                      <option value="business">Business</option>
                      <option value="enterprise">Enterprise</option>
                      <option value="custom">Custom</option>
                    </select>
                  </div>
                  <div className="space-y-2">
                    <label className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Nome Piano Custom</label>
                    <input
                      type="text"
                      value={editingClient.planName || ''}
                      onChange={(e) => setEditingClient({ ...editingClient, planName: e.target.value })}
                      placeholder="Es. Piano Alessandro"
                      className="w-full bg-white border border-slate-200 rounded-lg px-4 py-2.5 text-sm"
                    />
                  </div>
                  <div className="space-y-2">
                    <label className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Prezzo Mensile (EUR)</label>
                    <input
                      type="number"
                      value={editingClient.planPrice || ''}
                      onChange={(e) => setEditingClient({ ...editingClient, planPrice: Number(e.target.value) })}
                      placeholder="299"
                      className="w-full bg-white border border-slate-200 rounded-lg px-4 py-2.5 text-sm"
                    />
                  </div>
                  <div className="space-y-2">
                    <label className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Data Inizio Piano</label>
                    <input
                      type="date"
                      value={editingClient.planStartDate?.split('T')[0] || ''}
                      onChange={(e) => setEditingClient({ ...editingClient, planStartDate: e.target.value })}
                      className="w-full bg-white border border-slate-200 rounded-lg px-4 py-2.5 text-sm"
                    />
                  </div>
                </div>
              </div>

              {/* Limits */}
              <div>
                <h4 className="text-sm font-semibold text-slate-700 mb-3 flex items-center gap-2">
                  <BarChart3 className="h-4 w-4" /> Limiti di Utilizzo
                </h4>
                <div className="grid grid-cols-3 gap-4">
                  <div className="space-y-2">
                    <label className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Minuti/Mese</label>
                    <input
                      type="number"
                      value={editingClient.minutesLimit}
                      onChange={(e) => setEditingClient({ ...editingClient, minutesLimit: Number(e.target.value) })}
                      className="w-full bg-white border border-slate-200 rounded-lg px-4 py-2.5 text-sm"
                    />
                  </div>
                  <div className="space-y-2">
                    <label className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Voice IDs</label>
                    <input
                      type="number"
                      value={editingClient.voiceIdsLimit}
                      onChange={(e) => setEditingClient({ ...editingClient, voiceIdsLimit: Number(e.target.value) })}
                      className="w-full bg-white border border-slate-200 rounded-lg px-4 py-2.5 text-sm"
                    />
                  </div>
                  <div className="space-y-2">
                    <label className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Storage (GB)</label>
                    <input
                      type="number"
                      value={Math.round((editingClient.storageLimit || 0) / 1073741824)}
                      onChange={(e) => setEditingClient({ ...editingClient, storageLimit: Number(e.target.value) * 1073741824 })}
                      className="w-full bg-white border border-slate-200 rounded-lg px-4 py-2.5 text-sm"
                    />
                  </div>
                </div>
              </div>

              {/* Notes */}
              <div>
                <h4 className="text-sm font-semibold text-slate-700 mb-3">Note Admin</h4>
                <textarea
                  value={editingClient.notes || ''}
                  onChange={(e) => setEditingClient({ ...editingClient, notes: e.target.value })}
                  placeholder="Note interne sul cliente..."
                  rows={3}
                  className="w-full bg-white border border-slate-200 rounded-lg px-4 py-2.5 text-sm resize-none"
                />
              </div>
            </div>

            <div className="px-6 py-4 bg-slate-50 border-t border-slate-100 flex justify-end gap-3 sticky bottom-0">
              <button
                onClick={() => setEditingClient(null)}
                className="px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-100 rounded-lg transition-colors"
              >
                Annulla
              </button>
              <button
                onClick={handleSaveClient}
                disabled={isSaving}
                className="px-4 py-2 text-sm font-medium text-white bg-slate-900 hover:bg-slate-800 rounded-lg transition-colors flex items-center gap-2 disabled:opacity-50"
              >
                {isSaving ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Save className="h-4 w-4" />
                )}
                Salva Modifiche
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
